// React Imports
import type { ReactNode } from 'react';

// MUI Imports
import Chip from '@mui/material/Chip';
import type { ChipProps } from '@mui/material/Chip';

// Type Imports

import {
  HorizontalMenuDataType,
  HorizontalMenuItemDataType,
  HorizontalSubMenuDataType,
  VerticalMenuDataType,
  VerticalMenuItemDataType,
  VerticalSectionDataType,
  VerticalSubMenuDataType,
} from '../../types/menuTypes';
// Component Imports
import { MenuItem as HorizontalMenuItem, SubMenu as HorizontalSubMenu } from '../@menu/horizontal-menu';
import { MenuSection, MenuItem as VerticalMenuItem, SubMenu as VerticalSubMenu } from '../@menu/vertical-menu';

// Generate a menu from the menu data array
export const GenerateVerticalMenu = ({ menuData }: { menuData: VerticalMenuDataType[] }) => {
  // Hooks

  const renderMenuItems = (data: VerticalMenuDataType[]) => {
    // Use the map method to iterate through the array of menu data
    return data.map((item: VerticalMenuDataType, index) => {
      const menuSectionItem = item as VerticalSectionDataType;
      const subMenuItem = item as VerticalSubMenuDataType;
      const menuItem = item as VerticalMenuItemDataType;

      // Check if the current item is a section
      if (menuSectionItem.isSection) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { children, isSection, ...rest } = menuSectionItem;

        // If it is, return a MenuSection component and call generateMenu with the current menuSectionItem's children
        return (
          <MenuSection key={index} {...rest}>
            {children && renderMenuItems(children)}
          </MenuSection>
        );
      }

      // Check if the current item is a sub menu
      if (subMenuItem.children) {
        const { children, icon, prefix, suffix, ...rest } = subMenuItem;

        const Icon = icon ? <i className={icon} /> : null;

        const subMenuPrefix: ReactNode =
          prefix && (prefix as ChipProps).label ? (
            <Chip size="small" {...(prefix as ChipProps)} />
          ) : (
            (prefix as ReactNode)
          );

        const subMenuSuffix: ReactNode =
          suffix && (suffix as ChipProps).label ? (
            <Chip size="small" {...(suffix as ChipProps)} />
          ) : (
            (suffix as ReactNode)
          );

        // If it is, return a SubMenu component and call generateMenu with the current subMenuItem's children
        return (
          <VerticalSubMenu
            key={index}
            prefix={subMenuPrefix}
            suffix={subMenuSuffix}
            {...rest}
            {...(Icon && { icon: Icon })}
          >
            {children && renderMenuItems(children)}
          </VerticalSubMenu>
        );
      }

      // If the current item is neither a section nor a sub menu, return a MenuItem component
      const { label, icon, prefix, suffix, ...rest } = menuItem;

      // Localize the href
      const { href } = rest;

      const Icon = icon ? <i className={icon} /> : null;

      const menuItemPrefix: ReactNode =
        prefix && (prefix as ChipProps).label ? (
          <Chip size="small" {...(prefix as ChipProps)} />
        ) : (
          (prefix as ReactNode)
        );

      const menuItemSuffix: ReactNode =
        suffix && (suffix as ChipProps).label ? (
          <Chip size="small" {...(suffix as ChipProps)} />
        ) : (
          (suffix as ReactNode)
        );

      return (
        <VerticalMenuItem
          key={index}
          prefix={menuItemPrefix}
          suffix={menuItemSuffix}
          {...rest}
          href={href}
          {...(Icon && { icon: Icon })}
        >
          {label}
        </VerticalMenuItem>
      );
    });
  };

  return <>{renderMenuItems(menuData)}</>;
};

// Generate a menu from the menu data array
const _GenerateHorizontalMenu = ({ menuData }: { menuData: HorizontalMenuDataType[] }) => {
  // Hooks

  const renderMenuItems = (data: HorizontalMenuDataType[]) => {
    // Use the map method to iterate through the array of menu data
    return data.map((item: HorizontalMenuDataType, index) => {
      const subMenuItem = item as HorizontalSubMenuDataType;
      const menuItem = item as HorizontalMenuItemDataType;

      // Check if the current item is a sub menu
      if (subMenuItem.children) {
        const { children, icon, prefix, suffix, ...rest } = subMenuItem;

        const Icon = icon ? <i className={icon} /> : null;

        const subMenuPrefix: ReactNode =
          prefix && (prefix as ChipProps).label ? (
            <Chip size="small" {...(prefix as ChipProps)} />
          ) : (
            (prefix as ReactNode)
          );

        const subMenuSuffix: ReactNode =
          suffix && (suffix as ChipProps).label ? (
            <Chip size="small" {...(suffix as ChipProps)} />
          ) : (
            (suffix as ReactNode)
          );

        // If it is, return a SubMenu component and call generateMenu with the current subMenuItem's children
        return (
          <HorizontalSubMenu
            key={index}
            prefix={subMenuPrefix}
            suffix={subMenuSuffix}
            {...rest}
            {...(Icon && { icon: Icon })}
          >
            {children && renderMenuItems(children)}
          </HorizontalSubMenu>
        );
      }

      // If the current item is not a sub menu, return a MenuItem component
      const { label, icon, prefix, suffix, ...rest } = menuItem;

      // Localize the href
      const { href } = rest;

      const Icon = icon ? <i className={icon} /> : null;

      const menuItemPrefix: ReactNode =
        prefix && (prefix as ChipProps).label ? (
          <Chip size="small" {...(prefix as ChipProps)} />
        ) : (
          (prefix as ReactNode)
        );

      const menuItemSuffix: ReactNode =
        suffix && (suffix as ChipProps).label ? (
          <Chip size="small" {...(suffix as ChipProps)} />
        ) : (
          (suffix as ReactNode)
        );

      return (
        <HorizontalMenuItem
          key={index}
          prefix={menuItemPrefix}
          suffix={menuItemSuffix}
          {...rest}
          href={href}
          {...(Icon && { icon: Icon })}
        >
          {label}
        </HorizontalMenuItem>
      );
    });
  };

  return <>{renderMenuItems(menuData)}</>;
};
